/**
 * API client.
 *
 * The access token is held in memory only — never localStorage — so an XSS bug
 * cannot walk away with a durable credential. Durability comes from the
 * httpOnly refresh cookie, which JavaScript cannot read at all.
 */

let accessToken: string | null = null;
/** Whatever POST /api/auth/refresh returns: a token plus the session it belongs to. */
export type SessionBody = { accessToken?: string | null; [key: string]: any };

let refreshing: Promise<SessionBody | null> | null = null;
const listeners = new Set<(token: string | null) => void>();

export const setAccessToken = (token: string | null) => {
  accessToken = token;
  for (const fn of listeners) fn(token);
};
export const getAccessToken = () => accessToken;
export const onAuthChange = (fn: (token: string | null) => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export class ApiError extends Error {
  status: number;
  code: string;
  details?: Array<{ field: string; message: string }>;
  requestId?: string;

  constructor(status: number, body: any, fallback: string) {
    const error = body?.error ?? {};
    super(error.message || fallback);
    this.status = status;
    this.code = error.code ?? 'error';
    this.details = error.details;
    this.requestId = error.requestId;
  }

  /** Field-level messages, for showing errors next to the input that caused them. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const d of this.details ?? []) out[d.field] = d.message;
    return out;
  }
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  raw?: boolean;
  token?: string | null;
  retryOn401?: boolean;
};

/**
 * Exchange the refresh cookie for a fresh session.
 *
 * Strictly one at a time, and every caller must come through here. Refresh
 * tokens rotate, and the server treats a token presented twice as a stolen
 * one: it revokes the whole family and signs the user out. So a page with six
 * panels — or a provider React re-mounts — must produce one request, not six.
 * Returns the whole session body, since first-load needs the user and tenant
 * as well as the token.
 */
export function refreshSession(): Promise<SessionBody | null> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
      });
      if (!res.ok) return null;
      const body = (await res.json()) as SessionBody;
      setAccessToken(body.accessToken ?? null);
      return body;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function refreshAccessToken(): Promise<string | null> {
  return (await refreshSession())?.accessToken ?? null;
}

export async function request<T = any>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, raw = false, retryOn401 = true } = options;
  const token = options.token !== undefined ? options.token : accessToken;

  const headers: Record<string, string> = { 'X-Requested-With': 'XMLHttpRequest' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(path.startsWith('/') ? path : `/api/${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'include',
    signal,
  });

  if (res.status === 401 && retryOn401 && options.token === undefined) {
    const fresh = await refreshAccessToken();
    if (fresh) return request<T>(path, { ...options, retryOn401: false });
    setAccessToken(null);
  }

  if (raw) {
    if (!res.ok) throw new ApiError(res.status, await safeJson(res), res.statusText);
    return res as unknown as T;
  }

  if (res.status === 204) return undefined as T;

  const payload = await safeJson(res);
  if (!res.ok) throw new ApiError(res.status, payload, res.statusText);
  return payload as T;
}

const safeJson = async (res: Response) => {
  try { return await res.json(); } catch { return null; }
};

export const api = {
  get: <T = any>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T = any>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  patch: <T = any>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T = any>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  del: <T = any>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'DELETE' }),

  /** Download a file the API generates (report, label sheet, CSV export). */
  async download(path: string, options: RequestOptions & { filename?: string } = {}) {
    const res = await request<Response>(path, { ...options, raw: true });
    const blob = await res.blob();
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const name = options.filename ?? match?.[1] ?? 'download';

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick; Safari needs the URL to survive the click.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return name;
  },

  refresh: refreshAccessToken,
};

/** Turn a query object into a string, dropping empty values. */
export function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}
